// Google Analytics Tracking Utilities
// Measurement ID: G-TFJ3FKB2FH

// =============================================================================
// Pinterest Enhanced Match Helpers
// =============================================================================

/**
 * SHA256 hash using Web Crypto API (for Pinterest Enhanced Match)
 */
async function sha256(str) {
    const buffer = new TextEncoder().encode(str);
    const hashBuffer = await crypto.subtle.digest('SHA-256', buffer);
    const hashArray = Array.from(new Uint8Array(hashBuffer));
    return hashArray.map(b => b.toString(16).padStart(2, '0')).join('');
}

/**
 * Generate a unique event ID for Pinterest event deduplication
 * between browser tag and Conversions API.
 */
function generateEventId() {
    if (typeof crypto !== 'undefined' && crypto.randomUUID) {
        return crypto.randomUUID();
    }
    return Date.now().toString(36) + Math.random().toString(36).substr(2, 9);
}

const claimedEvents = new Set();

/** Prevent repeat browser events when a checkout or confirmation page is revisited. */
function claimBrowserEvent(storageName, key, value) {
    const token = `${key}:${value}`;
    if (claimedEvents.has(token)) return false;
    try {
        const storage = window[storageName];
        if (storage.getItem(key) === value) return false;
        storage.setItem(key, value);
    } catch (e) {
        // The in-memory guard still covers duplicate renders when storage is blocked.
    }
    claimedEvents.add(token);
    return true;
}

/**
 * Capture Pinterest click ID (epik) from URL and store in localStorage.
 * Pinterest appends ?epik=XXXXX to landing page URLs from ad clicks.
 * Uses localStorage so the value persists across sessions (epik is valid for 7 days).
 * Must be called on every page load to catch the landing.
 */
export function capturePinterestClickId() {
    try {
        const params = new URLSearchParams(window.location.search);
        const epik = params.get('epik');
        if (epik) {
            localStorage.setItem('pinterest_click_id', epik);
            // Also set it on the Pinterest tag immediately
            window.pintrk?.('set', { click_id: epik });
        }
    } catch (e) {
        // localStorage may be unavailable in private browsing
    }
}

/**
 * Get stored Pinterest click ID
 */
export function getPinterestClickId() {
    try {
        return localStorage.getItem('pinterest_click_id') || '';
    } catch {
        return '';
    }
}

/**
 * Set Pinterest Enhanced Match data (email + external_id).
 * Call this when user logs in, registers, or enters email at checkout.
 * Pinterest requires SHA256-hashed, lowercased email.
 */
export async function setPinterestEnhancedMatch(email, userId) {
    if (!email) return;

    try {
        const hashedEmail = await sha256(email.trim().toLowerCase());
        const enhancedData = { em: hashedEmail };

        if (userId) {
            const hashedId = await sha256(String(userId));
            enhancedData.external_id = hashedId;
        }

        // Add click_id if available
        const clickId = getPinterestClickId();
        if (clickId) {
            enhancedData.click_id = clickId;
        }

        // Store for use in individual events
        try {
            sessionStorage.setItem('pinterest_em', hashedEmail);
            if (userId) {
                sessionStorage.setItem('pinterest_external_id', await sha256(String(userId)));
            }
        } catch (e) {
            // sessionStorage unavailable
        }

        // Set via pintrk for all subsequent events
        window.pintrk?.('set', enhancedData);
    } catch (e) {
        // crypto.subtle may be unavailable on non-HTTPS
    }
}

/**
 * Ensure Pinterest enhanced match is set before firing an event.
 * Awaits the async hashing so params are ready when the event fires.
 */
async function ensurePinterestMatch(email, userId) {
    if (email) {
        await setPinterestEnhancedMatch(email, userId);
    }
    // Also set click_id on the tag if available
    const clickId = getPinterestClickId();
    if (clickId) {
        window.pintrk?.('set', { click_id: clickId });
    }
}

// =============================================================================
// Core Analytics
// =============================================================================

/**
 * Initialize analytics tracking
 */
export function initAnalytics() {
    // Capture Pinterest click ID on every page load
    capturePinterestClickId();

    if (document.readyState === 'loading') {
        document.addEventListener('DOMContentLoaded', () => {
            initScrollTracking();
            trackEngagementTime();
        });
    } else {
        initScrollTracking();
        trackEngagementTime();
    }
}

/**
 * Track page views
 */
export function trackPageView(pageTitle, pagePath) {
    if (window.gtag) {
        window.gtag('event', 'page_view', {
            page_title: pageTitle,
            page_path: pagePath
        });
    }
}

/**
 * Track custom events
 */
export function trackEvent(eventName, eventParams = {}) {
    if (window.gtag) {
        window.gtag('event', eventName, eventParams);
    }
}

/**
 * Track waitlist form interactions
 */
export function trackWaitlistFormStart() {
    trackEvent('form_start', {
        form_name: 'waitlist_form',
        form_destination: 'waitlist_signup'
    });
}

export function trackWaitlistFormSuccess() {
    // GA4 recommended event. Mark this as a key event in the GA4 interface.
    trackEvent('generate_lead', {
        currency: 'AUD',
        value: 1,
        lead_source: 'waitlist'
    });

    // Meta Pixel: record a real waitlist submission as a standard lead.
    window.fbq?.('track', 'Lead', {
        content_name: 'Waitlist signup',
    });

    // Pinterest: record a real waitlist lead without passing form data.
    window.pintrk?.('track', 'lead', {
        event_id: generateEventId(),
        lead_type: 'waitlist',
    });
}

export function trackWaitlistFormError(errorMessage) {
    trackEvent('form_error', {
        form_name: 'waitlist_form',
        error_message: errorMessage
    });
}

/**
 * Track CTA button clicks
 */
export function trackCTAClick(ctaName, ctaLocation) {
    trackEvent('cta_click', {
        cta_name: ctaName,
        cta_location: ctaLocation
    });
}

/**
 * Track scroll depth
 */
function initScrollTracking() {
    let scrollDepths = [25, 50, 75, 100];
    let triggeredDepths = new Set();

    window.addEventListener('scroll', () => {
        const scrollPercentage = Math.round(
            (window.scrollY / (document.documentElement.scrollHeight - window.innerHeight)) * 100
        );

        scrollDepths.forEach(depth => {
            if (scrollPercentage >= depth && !triggeredDepths.has(depth)) {
                triggeredDepths.add(depth);
                trackEvent('scroll_depth', {
                    percent_scrolled: depth
                });
            }
        });
    });
}

/**
 * Track section visibility
 */
export function trackSectionView(sectionName) {
    trackEvent('section_view', {
        section_name: sectionName
    });
}

/**
 * Track outbound links
 */
export function trackOutboundLink(url, linkText) {
    trackEvent('click', {
        event_category: 'outbound',
        event_label: linkText,
        transport_type: 'beacon',
        url: url
    });
}

/**
 * Track product interest
 */
export function trackProductInterest(productName) {
    trackEvent('view_item', {
        items: [{
            item_name: productName,
            item_category: 'skincare'
        }]
    });
}

// =============================================================================
// E-commerce Tracking (GA4 + Facebook Pixel + Pinterest)
// =============================================================================

/**
 * GA4 E-commerce: view_item (ViewContent)
 * Also fires Facebook Pixel ViewContent and Pinterest pagevisit
 */
export function trackViewItem(product) {
    const price = parseFloat(product.price || 0);

    trackEvent('view_item', {
        currency: 'AUD',
        value: price,
        items: [{
            item_id: product.id,
            item_name: product.name,
            item_category: 'skincare',
            price: price,
        }]
    });

    // Facebook Pixel: ViewContent
    window.fbq?.('track', 'ViewContent', {
        content_name: product.name,
        content_ids: [String(product.id)],
        content_type: 'product',
        value: price,
        currency: 'AUD',
    });

    // Pinterest: pagevisit (product view)
    const eventId = generateEventId();
    window.pintrk?.('track', 'pagevisit', {
        event_id: eventId,
        line_items: [{
            product_id: String(product.id),
            product_name: product.name,
            product_category: 'skincare',
            product_price: price,
        }],
        currency: 'AUD',
    });
}

/**
 * GA4 E-commerce: add_to_cart (AddToCart)
 * Also fires Facebook Pixel AddToCart and Pinterest addtocart
 */
export function trackAddToCart(product, quantity) {
    const price = parseFloat(product.price || 0);
    const value = price * quantity;

    trackEvent('add_to_cart', {
        currency: 'AUD',
        value: value,
        items: [{
            item_id: product.id,
            item_name: product.name,
            item_category: 'skincare',
            price: price,
            quantity: quantity,
        }]
    });

    // Facebook Pixel: AddToCart
    window.fbq?.('track', 'AddToCart', {
        content_name: product.name,
        content_ids: [String(product.id)],
        content_type: 'product',
        value: value,
        currency: 'AUD',
    });

    // Pinterest: addtocart
    const eventId = generateEventId();
    window.pintrk?.('track', 'addtocart', {
        event_id: eventId,
        value: value,
        order_quantity: quantity,
        currency: 'AUD',
        line_items: [{
            product_id: String(product.id),
            product_name: product.name,
            product_category: 'skincare',
            product_quantity: quantity,
            product_price: price,
        }],
    });
}

/**
 * GA4 E-commerce: begin_checkout (InitiateCheckout)
 * Also fires Facebook Pixel InitiateCheckout and Pinterest checkout
 */
export async function trackBeginCheckout(cart, email) {
    const signature = JSON.stringify({
        items: (cart.items || []).map(item => [item.product?.id, item.variant?.id, item.quantity]),
        subtotal: cart.subtotal,
    });
    if (!claimBrowserEvent('sessionStorage', 'melanua_meta_checkout', signature)) return;

    const items = (cart.items || []).map(item => ({
        item_id: item.product?.id,
        item_name: item.product?.name,
        item_category: 'skincare',
        price: parseFloat(item.product?.price || 0),
        quantity: item.quantity,
    }));
    const value = parseFloat(cart.subtotal || 0);
    const numItems = (cart.items || []).length;

    trackEvent('begin_checkout', {
        currency: 'AUD',
        value: value,
        items: items,
    });

    // Facebook Pixel: InitiateCheckout
    window.fbq?.('track', 'InitiateCheckout', {
        content_ids: items.map(i => String(i.item_id)),
        content_type: 'product',
        value: value,
        currency: 'AUD',
        num_items: numItems,
    });

    // Pinterest's Checkout event represents a completed order. It is fired
    // only in trackPurchase(), where a real order ID is available.
}

/**
 * GA4 E-commerce: purchase (Purchase)
 * Also fires Facebook Pixel Purchase and Pinterest checkout (purchase)
 */
export async function trackPurchase(order) {
    const orderId = String(order.id || order.order_number || '');
    if (!orderId || !claimBrowserEvent('localStorage', `melanua_meta_purchase_${orderId}`, 'sent')) return;

    const items = (order.items || []).map(item => ({
        item_id: item.product_id,
        item_name: item.product_name,
        item_category: 'skincare',
        price: parseFloat(item.unit_price || item.total_price || 0),
        quantity: item.quantity,
    }));
    const value = parseFloat(order.total || 0);
    const currency = order.currency || 'AUD';

    trackEvent('purchase', {
        transaction_id: order.order_number,
        currency: currency,
        value: value,
        shipping: parseFloat(order.shipping_cost || 0),
        items: items,
    });

    // Facebook Pixel: Purchase
    window.fbq?.('track', 'Purchase', {
        content_ids: items.map(i => String(i.item_id)),
        content_type: 'product',
        content_category: 'skincare',
        value: value,
        currency: currency,
        num_items: items.length,
    }, { eventID: `melanua-purchase-${orderId}` });

    // Set enhanced match BEFORE firing Pinterest event (await async hash)
    await ensurePinterestMatch(order.email);

    // Pinterest: checkout (purchase event)
    const eventId = generateEventId();
    window.pintrk?.('track', 'checkout', {
        event_id: eventId,
        value: value,
        currency: currency,
        order_id: order.order_number,
        order_quantity: items.length,
        line_items: items.map(i => ({
            product_id: String(i.item_id),
            product_name: i.item_name,
            product_category: 'skincare',
            product_quantity: i.quantity,
            product_price: i.price,
        })),
    });
}

/**
 * Track user engagement time
 */
function trackEngagementTime() {
    let startTime = Date.now();

    window.addEventListener('beforeunload', () => {
        const engagementTime = Math.round((Date.now() - startTime) / 1000);

        if (window.gtag) {
            window.gtag('event', 'user_engagement', {
                engagement_time_msec: engagementTime * 1000
            });
        }
    });
}
